import type { SelectionStepsDiff } from "@lib/selectionProcessDiff";
import { AlertTriangle, ArrowUpDown, Minus, Pencil, Plus, X } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";

interface ConfirmDiffDialogProps {
  open: boolean;
  diff: SelectionStepsDiff;
  // Coverage rows whose dimension is no longer in the current Assessment
  // Matrix and will be pruned on save (#2316). Surfaced as a notice in the
  // diff modal so the user is aware of the silent cleanup.
  droppedCoverageCount?: number;
  onCancel: () => void;
  onConfirm: () => void;
  busy?: boolean;
}

const labelOrStepType = (label: string | null, stepType: string): string =>
  label?.trim() ? label.trim() : stepType.replace(/_/g, " ");

const renderValue = (value: string): string => (value === "" ? "—" : value);

export function ConfirmDiffDialog({
  open,
  diff,
  droppedCoverageCount = 0,
  onCancel,
  onConfirm,
  busy = false,
}: ConfirmDiffDialogProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    },
    [onCancel],
  );

  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      // Defer focus to the next tick so the button is mounted in the DOM.
      queueMicrotask(() => cancelButtonRef.current?.focus());
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      if (open) previouslyFocusedRef.current?.focus?.();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  const hasChanges =
    diff.added.length > 0 ||
    diff.removed.length > 0 ||
    diff.reordered.length > 0 ||
    diff.edited.length > 0 ||
    droppedCoverageCount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="confirm-diff-dialog"
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss overlay */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onCancel}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="relative flex max-h-[80vh] w-full max-w-lg flex-col rounded-lg bg-card shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <h2 className="text-sm font-semibold text-text-primary">
              Confirm selection process changes
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
            aria-label="Close dialog"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 text-sm text-text-secondary">
          {!hasChanges && <p className="text-text-muted">No changes.</p>}

          {droppedCoverageCount > 0 && (
            <div
              data-testid="confirm-diff-dropped-coverage"
              className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            >
              {droppedCoverageCount === 1
                ? "1 coverage row referencing a dimension no longer in the matrix will be dropped."
                : `${droppedCoverageCount} coverage rows referencing dimensions no longer in the matrix will be dropped.`}
            </div>
          )}

          {diff.added.length > 0 && (
            <section className="mb-4" data-testid="confirm-diff-added">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
                <Plus size={14} /> Added
              </div>
              <ul className="space-y-1">
                {diff.added.map((row) => (
                  <li
                    key={`added-${row.stepType}-${row.label ?? ""}`}
                    className="text-text-primary"
                  >
                    {labelOrStepType(row.label, row.stepType)}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {diff.reordered.length > 0 && (
            <section className="mb-4" data-testid="confirm-diff-reordered">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
                <ArrowUpDown size={14} /> Reordered
              </div>
              <ul className="space-y-1">
                {diff.reordered.map((row) => (
                  <li key={`reordered-${row.continuityId}`} className="text-text-primary">
                    Step {row.fromIndex + 1} → Step {row.toIndex + 1}:{" "}
                    {labelOrStepType(row.label, row.stepType)}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {diff.removed.length > 0 && (
            <section className="mb-4" data-testid="confirm-diff-removed">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
                <Minus size={14} /> Removed
              </div>
              <ul className="space-y-2">
                {diff.removed.map((row) => {
                  const n = row.inFlightCandidateCount;
                  const candidateWord = n === 1 ? "candidate" : "candidates";
                  return (
                    <li key={`removed-${row.continuityId}`}>
                      <div className="text-text-primary">
                        {labelOrStepType(row.label, row.stepType)}
                      </div>
                      {n > 0 && (
                        <div className="text-xs text-text-muted">
                          {n} {candidateWord} currently on this step will remain pinned to it; no
                          journey loss.
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {diff.edited.length > 0 && (
            <section className="mb-2" data-testid="confirm-diff-edited">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
                <Pencil size={14} /> Edited
              </div>
              <ul className="space-y-3">
                {diff.edited.map((row) => (
                  <li key={`edited-${row.continuityId}`}>
                    <div className="text-text-primary">
                      {labelOrStepType(row.label, row.stepType)}
                    </div>
                    <ul className="mt-1 space-y-0.5 text-xs text-text-muted">
                      {row.fieldChanges.map((fc) => (
                        <li key={`${row.continuityId}-${fc.label}`}>
                          {fc.label}: {renderValue(fc.oldValue)} → {renderValue(fc.newValue)}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            className="rounded border border-border bg-card px-3 py-1 text-xs text-text-secondary hover:bg-card-hover"
            data-testid="confirm-diff-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
            data-testid="confirm-diff-apply"
          >
            {busy ? "Applying..." : "Apply changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
